import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, ArrowLeft, FileText, Wand2,
  ChevronDown, ChevronRight, Plus, Trash2, Lock, FileDown, Mail, History,
  RotateCw, AlertCircle, X, ClipboardCheck, Sparkles, MoreHorizontal, Upload,
  Gauge, FolderOpen,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { DatePickerField } from '../components/ui/date-picker-field';
import RichTextEditor, { plainTextToMarkdown } from '../components/RichTextEditor';
import {
  BulkImportProvider,
  useBulkImport,
  useRegisterBulkImportField,
} from '../components/BulkFileImportDialog';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import TranscriptImportDialog from '../components/TranscriptImportDialog';
import SourceRecordingsCard from '../components/SourceRecordingsCard';
import VerifyImportsDialog from '../components/VerifyImportsDialog';
import WorkspaceSubNav, { computeEstimateProgress } from '../components/WorkspaceSubNav';
import MaturityBaselineDialog from '../components/MaturityBaselineDialog';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// One-time, best-effort plain-text → Markdown coercion for legacy
// requirements docs. Walks the catalog taxonomy and, for every field
// declared as `long-text`, runs the stored value through
// `plainTextToMarkdown` if the value still looks like flat text (no
// markdown markers, no inline HTML). New rich content is left alone.
// Runs on load only — never persisted until the user edits + saves.
function _walkLongTextFields(doc, catalog, mutate) {
  if (!doc || !catalog) return doc;
  const tax = catalog.narrative_taxonomy || [];
  const visitFields = (fields, container) => {
    for (const f of (fields || [])) {
      if (!f || !f.id) continue;
      if (f.kind === 'long-text' && typeof container[f.id] === 'string') {
        container[f.id] = mutate(container[f.id]);
      } else if (f.kind === 'table' && Array.isArray(container[f.id])) {
        const cols = f.columns || [];
        for (const row of container[f.id]) {
          for (const c of cols) {
            if (c.kind === 'long-text' && typeof row[c.id] === 'string') {
              row[c.id] = mutate(row[c.id]);
            }
          }
        }
      } else if (f.kind === 'mapped_section' && f.fields) {
        // Mapped sections share storage with top-level keys
        visitFields(f.fields, container);
      }
    }
  };
  for (const section of tax) {
    if (!section || !section.id) continue;
    const bucket = doc[section.id];
    if (bucket && typeof bucket === 'object') {
      visitFields(section.fields, bucket);
    } else if (section.fields && doc) {
      // Some sections write directly into doc root (mapped sections)
      visitFields(section.fields, doc);
    }
  }
  return doc;
}

function coerceLegacyLongText(doc, catalog) {
  if (!doc || !catalog) return doc;
  // Clone to avoid mutating the response object directly
  const clone = JSON.parse(JSON.stringify(doc));
  _walkLongTextFields(clone, catalog, plainTextToMarkdown);
  // Also walk every component-checklist `selections[].comment` field —
  // those moved from <Input> (plain text) to RichTextEditor (Markdown)
  // in v260.48, so any legacy estimate has plain-text comments that
  // should auto-coerce to Markdown on first load (no persistence until
  // the user saves).
  const checklistKeys = [
    'functional_requirements',
    'reporting_dashboards',
    'data_migration',
    'integrations',
    'mobile_offline',
  ];
  for (const key of checklistKeys) {
    const bucket = clone[key];
    if (!bucket || typeof bucket !== 'object') continue;
    for (const subKey of Object.keys(bucket)) {
      const list = bucket[subKey];
      if (!Array.isArray(list)) continue;
      for (const sel of list) {
        if (sel && typeof sel === 'object' && typeof sel.comment === 'string' && sel.comment) {
          sel.comment = plainTextToMarkdown(sel.comment);
        }
      }
    }
  }
  return clone;
}

// v260.2 — fully sequential numbered document. Mapped sections
// (Timeline / Maturity / Functional / OJT / Integrations / Migration)
// are embedded inline inside their parent narrative section via
// kind: "mapped_section" in the taxonomy. Storage stays on the original
// top-level keys (timeline_targets, maturity_assessment, functional_requirements,
// ojt_requirements, integrations, migration_scope, data_migration).
export default function RequirementsFormPage() {
  return (
    <BulkImportProvider>
      <RequirementsFormPageInner />
    </BulkImportProvider>
  );
}

// Small self-contained chip showing how many source documents are
// attached to this estimate, linking through to the Documents tab.
function SourceDocumentsChip({ estimateId, navigate }) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    if (!estimateId) return undefined;
    let mounted = true;
    axios
      .get(`${API_URL}/api/estimates/${estimateId}/source-documents`, { withCredentials: true })
      .then((r) => { if (mounted) setCount((r.data.documents || []).length); })
      .catch(() => { /* non-blocking */ });
    return () => { mounted = false; };
  }, [estimateId]);

  if (count === null || count === 0) return null;
  return (
    <button
      type="button"
      onClick={() => navigate(`/workspace/${estimateId}/documents`)}
      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 hover:border-[#4ABFCF] hover:text-[#0F2040] transition-colors"
      data-testid="requirements-source-documents-chip"
      title="View the source documents attached to this estimate"
    >
      <FolderOpen className="w-3.5 h-3.5 text-[#4ABFCF]" />
      Source Documents ({count})
    </button>
  );
}

function RequirementsFormPageInner() {
  const { id: estimateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [doc, setDoc] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [applyPreview, setApplyPreview] = useState(null);
  // v260.33 — Phase 2: narrative-driven component / integration
  // suggestions. Keyed by item.id ("comp::<Name>" or "int::<type>") so
  // SelectableRow can render a sparkle pill on rows the user hasn't
  // selected yet but the narrative implies they should consider.
  const [suggestions, setSuggestions] = useState({});
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [transcriptDialogOpen, setTranscriptDialogOpen] = useState(false);
  const [resumeJobId, setResumeJobId] = useState(null);
  // v260.131 — Resumable background transcript jobs: surface a badge so a
  // long analysis can be reopened/applied after the dialog is closed.
  const [latestJob, setLatestJob] = useState(null);
  const prevJobStatusRef = useRef(null);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [openSections, setOpenSections] = useState({});
  // v260.107 — "Import from RFP folder" — hidden file picker. The
  // user clicks the menu item, we expand every long-text section so
  // each field registers itself with the bulk-import context, then
  // hand the picked files to the wizard one tick later.
  const rfpInputRef = useRef(null);
  const { openWithFiles: openBulkImportWithFiles } = useBulkImport();

  // ── v260.131 — Resumable transcript jobs ──────────────────────────
  const refreshLatestJob = useCallback(async () => {
    if (!estimateId) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/estimates/${estimateId}/requirements/parse-transcript/jobs-latest`,
        { withCredentials: true },
      );
      const job = res.data?.job || null;
      // Toast once when a background analysis finishes while the dialog
      // is closed (processing → done transition).
      if (prevJobStatusRef.current === 'processing' && job?.status === 'done') {
        toast.success('Transcript analysis ready — click "Review" to apply it.');
      }
      prevJobStatusRef.current = job?.status || null;
      setLatestJob(job);
    } catch { /* non-blocking */ }
  }, [estimateId]);

  useEffect(() => { refreshLatestJob(); }, [refreshLatestJob]);

  // Poll while a job is processing so the badge flips to "ready" on its own.
  useEffect(() => {
    if (latestJob?.status !== 'processing') return undefined;
    const id = setInterval(refreshLatestJob, 5000);
    return () => clearInterval(id);
  }, [latestJob?.status, refreshLatestJob]);

  const dismissLatestJob = async () => {
    const jobId = latestJob?.job_id;
    if (!jobId) return;
    try {
      await axios.post(
        `${API_URL}/api/estimates/${estimateId}/requirements/parse-transcript/jobs/${jobId}/consume`,
        {}, { withCredentials: true },
      );
    } catch { /* best-effort */ }
    prevJobStatusRef.current = null;
    setLatestJob(null);
  };

  const handleRfpFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';  // reset so picking again works
    if (files.length === 0) return;
    // Expand every narrative section so all long-text fields mount &
    // register before the wizard reads the registry. We wait two
    // `requestAnimationFrame`s to let React paint the newly-mounted
    // sections + their RTE children before opening the dialog.
    setOpenSections((prev) => {
      const next = { ...prev };
      (catalog.narrative_taxonomy || []).forEach((sec) => { next[sec.id] = true; });
      return next;
    });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      openBulkImportWithFiles(files);
    }));
  };
  // Whether this estimate uses the modern Conditions Flow — controls
  // whether the "Project Conditions" tab in the workspace sub-nav is
  // enabled or rendered disabled with a tooltip.
  const [hasConditions, setHasConditions] = useState(false);
  // Full estimate doc — drives the sub-nav context strip
  // (customer · project) and the per-tab progress dots.
  const [estimate, setEstimate] = useState(null);

  // v260.12 — Revision-nudge: prompts the user to drop a fresh row
  // into Document Control after they've made a meaningful batch of
  // edits OR if the latest revision row is older than today.
  const REVISION_EDIT_THRESHOLD = 10;
  const [editsSinceLastRevision, setEditsSinceLastRevision] = useState(0);
  const [revisionNudgeDismissed, setRevisionNudgeDismissed] = useState(false);
  const [revisionDraft, setRevisionDraft] = useState('');

  // ── Initial load: doc + catalog + estimate (for has_conditions) in parallel ──
  useEffect(() => {
    let alive = true;
    Promise.all([
      axios.get(`${API_URL}/api/estimates/${estimateId}/requirements`, { withCredentials: true }),
      axios.get(`${API_URL}/api/estimates/${estimateId}/requirements/catalog`, { withCredentials: true }),
      axios.get(`${API_URL}/api/estimates/${estimateId}`, { withCredentials: true })
        .catch(() => ({ data: {} })), // non-fatal — sub-nav still renders without it
    ]).then(([r1, r2, r3]) => {
      if (!alive) return;
      setDoc(coerceLegacyLongText(r1.data.requirements_doc, r2.data));
      setCatalog(r2.data);
      setHasConditions(!!r3.data?.has_conditions);
      setEstimate(r3.data || null);
      // Default-open the first section (Document Control) so first-time users
      // see something on load. Everything else starts collapsed.
      const first = (r2.data.narrative_taxonomy || [])[0];
      if (first) setOpenSections({ [first.id]: true });
    }).catch((e) => {
      toast.error('Failed to load requirements doc: ' + (e.response?.data?.detail || e.message));
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [estimateId]);

  // ── Debounced autosave on `doc` change ──
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (!doc || !dirtyRef.current) return;
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.put(`${API_URL}/api/estimates/${estimateId}/requirements`,
          { requirements_doc: doc }, { withCredentials: true });
        setLastSavedAt(new Date());
        dirtyRef.current = false;
        // v260.33 — refresh narrative-driven suggestions after each
        // successful save. Non-blocking: a failure here just leaves the
        // previous suggestion set in place.
        axios.get(`${API_URL}/api/estimates/${estimateId}/requirements/suggestions`,
          { withCredentials: true })
          .then((r) => {
            const next = {};
            for (const s of (r.data?.suggestions || [])) next[s.id] = s;
            setSuggestions(next);
          })
          .catch(() => {});
      } catch (e) {
        toast.error('Autosave failed: ' + (e.response?.data?.detail || e.message));
      } finally { setSaving(false); }
    }, 800);
    return () => clearTimeout(t);
  }, [doc, estimateId]);

  // ── v260.33 — Initial suggestions fetch (post-load) ──
  useEffect(() => {
    if (!doc) return;
    let alive = true;
    axios.get(`${API_URL}/api/estimates/${estimateId}/requirements/suggestions`,
      { withCredentials: true })
      .then((r) => {
        if (!alive) return;
        const next = {};
        for (const s of (r.data?.suggestions || [])) next[s.id] = s;
        setSuggestions(next);
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId, !!doc]);

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
    setEditsSinceLastRevision((n) => n + 1);
  }, []);

  const setSection = useCallback((key, value) => {
    setDoc((prev) => ({ ...prev, [key]: value }));
    markDirty();
  }, [markDirty]);

  const setField = useCallback((sectionKey, field, value) => {
    setDoc((prev) => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] || {}), [field]: value },
    }));
    markDirty();
  }, [markDirty]);

  const toggleSelection = useCallback((sectionKey, itemId, patch) => {
    setDoc((prev) => {
      const arr = [...(prev[sectionKey] || [])];
      const idx = arr.findIndex((x) => x.id === itemId);
      if (idx >= 0) arr[idx] = { ...arr[idx], ...patch };
      else arr.push({ id: itemId, selected: false, comment: '', complexity: '', ...patch });
      return { ...prev, [sectionKey]: arr };
    });
    markDirty();
  }, [markDirty]);

  // v260.34 — bulk-accept all narrative-driven suggestions for a section
  // in a single state mutation. Used by the "Accept all N suggestions"
  // header pill on each mapped block (Functional / OJT / Integrations /
  // Migration). Patch applied: `selected: true` (and the existing
  // comment / complexity are preserved if the row already exists).
  const bulkAcceptSuggestions = useCallback((sectionKey, itemIds) => {
    if (!Array.isArray(itemIds) || itemIds.length === 0) return;
    setDoc((prev) => {
      const arr = [...(prev[sectionKey] || [])];
      for (const itemId of itemIds) {
        const idx = arr.findIndex((x) => x.id === itemId);
        if (idx >= 0) arr[idx] = { ...arr[idx], selected: true };
        else arr.push({ id: itemId, selected: true, comment: '', complexity: '' });
      }
      return { ...prev, [sectionKey]: arr };
    });
    markDirty();
    toast.success(`Accepted ${itemIds.length} suggestion${itemIds.length === 1 ? '' : 's'}`);
  }, [markDirty]);

  // v260.35 — Dismiss a single narrative-driven suggestion for this
  // estimate. Persists into doc.dismissed_suggestions so the row's
  // Suggested pill, the Accept-all bar, and the apply auto-flag all
  // stop nagging on subsequent runs. Toast includes an Undo action.
  const undismissSuggestion = useCallback((itemId) => {
    setDoc((prev) => ({
      ...prev,
      dismissed_suggestions: (prev.dismissed_suggestions || []).filter((x) => x !== itemId),
    }));
    markDirty();
  }, [markDirty]);

  const dismissSuggestion = useCallback((itemId) => {
    setDoc((prev) => {
      const cur = Array.isArray(prev.dismissed_suggestions) ? prev.dismissed_suggestions : [];
      if (cur.includes(itemId)) return prev;
      return { ...prev, dismissed_suggestions: [...cur, itemId] };
    });
    // Drop locally so the pill disappears immediately without waiting
    // for the next /suggestions roundtrip.
    setSuggestions((prev) => {
      if (!(itemId in prev)) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    markDirty();
    toast.message('Suggestion dismissed', {
      action: { label: 'Undo', onClick: () => undismissSuggestion(itemId) },
    });
  }, [markDirty, undismissSuggestion]);

  // v260.36 — Bulk-restore every dismissed suggestion whose item belongs
  // to a given mapped section. Powers the "N dismissed · Restore" link
  // in each section header so PMs can reverse silenced picks without
  // hunting through old toasts.
  const restoreDismissedInSection = useCallback((sectionItemIds) => {
    if (!Array.isArray(sectionItemIds) || sectionItemIds.length === 0) return;
    const ids = new Set(sectionItemIds);
    const removing = (doc?.dismissed_suggestions || []).filter((x) => ids.has(x));
    if (removing.length === 0) return;
    setDoc((prev) => ({
      ...prev,
      dismissed_suggestions: (prev.dismissed_suggestions || []).filter((x) => !ids.has(x)),
    }));
    markDirty();
    toast.success(`Restored ${removing.length} dismissed suggestion${removing.length === 1 ? '' : 's'}`);
  }, [doc, markDirty]);

  const findSelection = useCallback((sectionKey, itemId) => {
    return (doc?.[sectionKey] || []).find((x) => x.id === itemId)
      || { selected: false, comment: '', complexity: '' };
  }, [doc]);

  // ── Narrative setter: doc.narrative[sectionId][fieldId] = value ──
  const setNarrativeField = useCallback((sectionId, fieldId, value) => {
    setDoc((prev) => {
      const narrative = { ...(prev.narrative || {}) };
      narrative[sectionId] = { ...(narrative[sectionId] || {}), [fieldId]: value };
      return { ...prev, narrative };
    });
    markDirty();
  }, [markDirty]);

  // ── Apply ──
  const handleApply = async () => {
    setApplying(true);
    try {
      if (dirtyRef.current) {
        await axios.put(`${API_URL}/api/estimates/${estimateId}/requirements`,
          { requirements_doc: doc }, { withCredentials: true });
        dirtyRef.current = false;
      }
      const r = await axios.post(`${API_URL}/api/estimates/${estimateId}/requirements/apply`,
        {}, { withCredentials: true });
      setApplyPreview(r.data);
    } catch (e) {
      toast.error('Apply failed: ' + (e.response?.data?.detail || e.message));
    } finally { setApplying(false); }
  };

  // ── Export PDF ──
  const handleExportPdf = async () => {
    await runExport('pdf', 'application/pdf', setExportingPdf);
  };

  // ── Export DOCX ──
  const handleExportDocx = async () => {
    await runExport(
      'docx',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      setExportingDocx,
    );
  };

  const runExport = async (kind, mime, setBusy) => {
    setBusy(true);
    try {
      if (dirtyRef.current) {
        await axios.put(`${API_URL}/api/estimates/${estimateId}/requirements`,
          { requirements_doc: doc }, { withCredentials: true });
        dirtyRef.current = false;
      }
      const r = await axios.get(
        `${API_URL}/api/estimates/${estimateId}/requirements/export/${kind}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const cd = r.headers['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `requirements.${kind}`;
      const url = URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`Requirements ${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`${kind.toUpperCase()} export failed: ` + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  // ── Mapped-section renderers used inline within narrative sections ──
  const mappedCtx = useMemo(() => ({
    doc, catalog, setField, setSection, toggleSelection, findSelection,
    suggestions, bulkAcceptSuggestions, dismissSuggestion, restoreDismissedInSection,
    estimate,
  }), [doc, catalog, setField, setSection, toggleSelection, findSelection,
       suggestions, bulkAcceptSuggestions, dismissSuggestion, restoreDismissedInSection,
       estimate]);

  // ── v260.12 — Revision-nudge surface ──
  const revisionNudge = useMemo(() => {
    if (!doc || !catalog || revisionNudgeDismissed) return null;
    const revisions = ((doc.narrative || {}).document_control || {}).revisions || [];
    const todayIso = new Date().toISOString().slice(0, 10);
    const latest = revisions[revisions.length - 1];
    const latestDate = (latest?.date || '').trim();
    const hitEditThreshold = editsSinceLastRevision >= REVISION_EDIT_THRESHOLD;
    // Stale only if the latest row carries an actual date and it's not today.
    // Empty seed dates fall through to the edit-threshold path so we don't
    // nag the user the second they open a fresh form.
    const staleLastRevision = !!latestDate && latestDate !== todayIso;
    if (!hitEditThreshold && !staleLastRevision) return null;
    return {
      reason: hitEditThreshold
        ? `${editsSinceLastRevision} edits since the last revision entry`
        : `Last revision row dates back to ${latestDate}`,
    };
  }, [doc, catalog, revisionNudgeDismissed, editsSinceLastRevision]);

  const addRevisionEntry = useCallback(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const ownerName = (user?.name || user?.email || 'Unknown').trim();
    const newRow = {
      date: todayIso,
      change_description: revisionDraft.trim() || 'Major update',
      change_owner: ownerName,
    };
    setDoc((prev) => {
      const narrative = { ...(prev.narrative || {}) };
      const dc = { ...(narrative.document_control || {}) };
      const revisions = Array.isArray(dc.revisions) ? [...dc.revisions] : [];
      revisions.push(newRow);
      dc.revisions = revisions;
      narrative.document_control = dc;
      return { ...prev, narrative };
    });
    dirtyRef.current = true;
    setEditsSinceLastRevision(0);
    setRevisionDraft('');
    setOpenSections((o) => ({ ...o, document_control: true }));
    toast.success('Revision entry added to Document Control');
  }, [user, revisionDraft]);

  // v260.16 — Apply AI-parsed transcript sections into the form.
  // v260.18 — Also threads through any new audio attachment record so
  // the Source Recordings card renders the inline player + download.
  // v260.19 — Merges the per-field provenance map so chips render
  // alongside each AI-imported field.
  const handleApplyTranscript = useCallback((pickedSections, newAttachment, provenance) => {
    setDoc((prev) => {
      const narrative = { ...(prev.narrative || {}) };
      for (const [secId, fields] of Object.entries(pickedSections)) {
        narrative[secId] = { ...(narrative[secId] || {}), ...fields };
      }
      const attachments = Array.isArray(prev.attachments) ? [...prev.attachments] : [];
      if (newAttachment && !attachments.some((a) => a.id === newAttachment.id)) {
        attachments.push(newAttachment);
      }
      // Merge provenance for the picked sections only — don't pull in
      // provenance for sections the user opted out of.
      const fieldProvenance = { ...(prev.field_provenance || {}) };
      if (provenance) {
        for (const secId of Object.keys(pickedSections)) {
          if (provenance[secId]) {
            fieldProvenance[secId] = { ...(fieldProvenance[secId] || {}), ...provenance[secId] };
          }
        }
      }
      return { ...prev, narrative, attachments, field_provenance: fieldProvenance };
    });
    dirtyRef.current = true;
    setEditsSinceLastRevision((n) => n + Object.keys(pickedSections).length);
    setOpenSections((o) => {
      const next = { ...o };
      for (const secId of Object.keys(pickedSections)) next[secId] = true;
      return next;
    });
  }, []);

  // v260.18 — Drop an attachment from the doc state after the backend
  // confirms deletion. The doc autosaves so this hits the server too.
  const handleAttachmentDeleted = useCallback((attachmentId) => {
    setDoc((prev) => {
      // Also clean any provenance entries that pointed at this attachment
      // so chips don't dangle on a missing recording.
      const fp = { ...(prev.field_provenance || {}) };
      for (const [secId, fields] of Object.entries(fp)) {
        const cleaned = Object.fromEntries(
          Object.entries(fields).filter(([, v]) => v?.attachment_id !== attachmentId),
        );
        if (Object.keys(cleaned).length) fp[secId] = cleaned;
        else delete fp[secId];
      }
      return {
        ...prev,
        attachments: (prev.attachments || []).filter((a) => a.id !== attachmentId),
        field_provenance: fp,
      };
    });
    dirtyRef.current = false; // backend already pulled it; no FE write needed
  }, []);

  // v260.19 — Jump the inline `<audio>` player to the start time of an
  // AI-imported field's source quote. Triggered from the provenance
  // chip next to each AI-imported field.
  const jumpToProvenance = useCallback((prov) => {
    if (!prov || !prov.attachment_id) {
      toast.error('No source recording linked to this field');
      return;
    }
    // Force the Source Recordings card open via its DOM toggle if it's
    // currently collapsed (the audio element only mounts when open).
    const audioEl = document.querySelector(
      `[data-testid="source-recording-audio-${prov.attachment_id}"]`,
    );
    if (!audioEl) {
      const toggle = document.querySelector('[data-testid="source-recordings-toggle"]');
      if (toggle && toggle.getAttribute('aria-expanded') === 'false') toggle.click();
      // Re-query after a tick once React renders the audio element.
      setTimeout(() => {
        const el = document.querySelector(
          `[data-testid="source-recording-audio-${prov.attachment_id}"]`,
        );
        if (!el) { toast.error('Source recording not available'); return; }
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (prov.start_seconds != null) el.currentTime = prov.start_seconds;
        el.play().catch(() => { /* autoplay block — user can press play manually */ });
      }, 80);
      return;
    }
    audioEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (prov.start_seconds != null) audioEl.currentTime = prov.start_seconds;
    audioEl.play().catch(() => { /* autoplay block ok */ });
  }, []);

  // v260.20 — Verify-import action handler. Approve clears the
  // provenance chip (field is now "human-verified"). Reject clears
  // the field value AND the provenance chip. Skip is a no-op handled
  // entirely inside the dialog.
  // v260.21 — Approve ALSO stamps a permanent `field_verifications`
  // entry so the green ✓ chip surfaces forever alongside the value.
  const handleVerifyAction = useCallback((sectionId, fieldId, action) => {
    if (action === 'skip') return;
    setDoc((prev) => {
      const next = { ...prev };
      // Remove the provenance entry either way.
      const fp = { ...(prev.field_provenance || {}) };
      if (fp[sectionId]) {
        const { [fieldId]: _removed, ...rest } = fp[sectionId];
        if (Object.keys(rest).length) fp[sectionId] = rest;
        else delete fp[sectionId];
      }
      next.field_provenance = fp;

      // Update verifications: stamp on approve, clear on reject.
      const fv = { ...(prev.field_verifications || {}) };
      if (action === 'approve') {
        const stamp = {
          verified_by: (user?.name || user?.email || 'Unknown').trim(),
          verified_at: new Date().toISOString(),
        };
        fv[sectionId] = { ...(fv[sectionId] || {}), [fieldId]: stamp };
      } else if (action === 'reject') {
        if (fv[sectionId]) {
          const { [fieldId]: _stamp, ...restFv } = fv[sectionId];
          if (Object.keys(restFv).length) fv[sectionId] = restFv;
          else delete fv[sectionId];
        }
      }
      next.field_verifications = fv;

      // On reject also clear the value.
      if (action === 'reject') {
        const narr = { ...(prev.narrative || {}) };
        if (narr[sectionId]) {
          const { [fieldId]: _v, ...restNarr } = narr[sectionId];
          narr[sectionId] = restNarr;
        }
        next.narrative = narr;
      }
      return next;
    });
    dirtyRef.current = true;
  }, [user]);

  // v260.20 — Total count of fields still flagged as AI-imported.
  const aiImportedCount = useMemo(() => {
    const fp = doc?.field_provenance || {};
    return Object.values(fp).reduce((n, fields) => n + Object.keys(fields).length, 0);
  }, [doc]);

  if (loading || !doc || !catalog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Workspace sub-nav so the natural flow
          (Requirements → Project Conditions → Estimate → …) is one
          click away regardless of where the user landed. */}
      <WorkspaceSubNav
        estimateId={estimateId}
        activeTab="requirements"
        hasConditions={hasConditions}
        customerName={estimate?.organization_name}
        projectName={estimate?.implementation_name || estimate?.estimate_name}
        progress={computeEstimateProgress({
          ...estimate,
          // The requirements_doc state in `doc` is more current than the
          // server snapshot in `estimate.requirements_doc` (user may have
          // edited fields without saving yet), so prefer it.
          requirements_doc: doc || estimate?.requirements_doc || null,
        })}
        onProjectConditionsClick={() => {
          // RequirementsFormPage is outside the workspace router, so
          // we route into the workspace and ask the layout to open the
          // EditConditionsDialog via its `open-project-conditions`
          // window event listener.
          navigate(`/workspace/${estimateId}/overview`);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('open-project-conditions'));
          }, 100);
        }}
      />
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="w-full px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/estimator?load=${estimateId}`)} data-testid="requirements-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Estimator
            </Button>
            <div className="border-l border-slate-200 h-6" />
            <div>
              <h1 className="text-lg font-semibold text-[#0F2040] flex items-center gap-2">
                <FileText className="w-4 h-4 text-cyan-600" />
                {catalog.is_project ? 'Project Requirements' : 'Implementation Requirements'}
              </h1>
              <div className="text-xs text-slate-500 flex items-center gap-2">
                {saving ? (<><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>)
                  : lastSavedAt ? (<><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Saved · {lastSavedAt.toLocaleTimeString()}</>)
                  : 'Autosaves as you type'}
                {doc?.last_applied_at && (
                  <span className="ml-2 text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full text-[10px]">
                    Last applied {new Date(doc.last_applied_at).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SourceDocumentsChip estimateId={estimateId} navigate={navigate} />
            {/* v260.131 — Resumable transcript job badge */}
            {latestJob?.status === 'processing' && (
              <span
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-700"
                data-testid="transcript-job-processing"
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing transcript…
              </span>
            )}
            {latestJob?.status === 'done' && (
              <button
                type="button"
                onClick={() => { setResumeJobId(latestJob.job_id); setTranscriptDialogOpen(true); }}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-700 hover:bg-emerald-100 transition-colors"
                data-testid="transcript-job-ready"
              >
                <Sparkles className="w-3.5 h-3.5" /> Transcript ready — Review
              </button>
            )}
            {latestJob?.status === 'error' && (
              <span
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700"
                data-testid="transcript-job-error"
              >
                <AlertCircle className="w-3.5 h-3.5" /> Transcript analysis failed
                <button onClick={dismissLatestJob} className="ml-1 hover:text-rose-900" data-testid="transcript-job-dismiss" title="Dismiss">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {/* Secondary actions consolidated into a single Actions menu
                — keeps the header uncluttered now that the operational
                shell uses full width. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="requirements-actions-trigger"
                >
                  <MoreHorizontal className="w-4 h-4 mr-2" />
                  Actions
                  <ChevronDown className="w-3.5 h-3.5 ml-1.5 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="requirements-actions-menu">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                  Import
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => { setResumeJobId(null); setTranscriptDialogOpen(true); }}
                  className="text-cyan-700 focus:text-cyan-800"
                  data-testid="requirements-import-transcript-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Import from Transcript
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => rfpInputRef.current?.click()}
                  className="text-cyan-700 focus:text-cyan-800"
                  data-testid="requirements-import-rfp-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> Import from RFP folder…
                </DropdownMenuItem>
                {/* v260.107 — Hidden file picker behind the RFP menu
                    item. Accepts the same set as the drag-drop path
                    so users get a consistent experience whether they
                    drag files or click. */}
                <input
                  ref={rfpInputRef}
                  type="file"
                  multiple
                  accept=".md,.markdown,.txt,.docx"
                  className="hidden"
                  onChange={handleRfpFiles}
                  data-testid="requirements-import-rfp-input"
                />
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                  Export
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={handleExportPdf}
                  disabled={exportingPdf}
                  data-testid="requirements-export-pdf-btn"
                >
                  {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                  Export PDF
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleExportDocx}
                  disabled={exportingDocx}
                  data-testid="requirements-export-docx-btn"
                >
                  {exportingDocx ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                  Export DOCX
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                  Share
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => setEmailDialogOpen(true)}
                  data-testid="requirements-email-btn"
                >
                  <Mail className="w-4 h-4 mr-2" /> Email
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setHistoryDialogOpen(true)}
                  data-testid="requirements-history-btn"
                >
                  <History className="w-4 h-4 mr-2" /> Email History
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {aiImportedCount > 0 && (
              <Button
                variant="outline"
                onClick={() => setVerifyDialogOpen(true)}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="requirements-verify-imports-btn"
                title="Step through every AI-imported field and approve/reject"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Verify AI Imports
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold">
                  {aiImportedCount}
                </span>
              </Button>
            )}
            {!catalog.is_project && (
            <Button
              onClick={handleApply}
              disabled={applying}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="requirements-apply-btn"
            >
              {applying ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</>)
                : (<><Wand2 className="w-4 h-4 mr-2" /> Apply to Estimate</>)}
            </Button>
            )}
          </div>
        </div>
      </div>

      <div className="w-full px-6 py-6 space-y-3">
        {/* Tip banner */}
        <div className="bg-cyan-50 border border-cyan-200 rounded-lg px-4 py-3 text-xs text-slate-700" data-testid="requirements-tip">
          <strong className="text-cyan-800">Tip:</strong>{' '}
          {catalog.is_project ? (
            <>Project estimates use this slimmed-down requirements form — capture the{' '}
            <strong>Business Case</strong>, <strong>Technical / Functional Requirements</strong>,{' '}
            <strong>Testing / Validation Approach</strong> and <strong>Documentation</strong>.
            These sections ship with your Quote &amp; SOW exports.</>
          ) : (
            <>This is a single, sequentially numbered Requirements Document. Sub-sections labelled
            <em> &ldquo;writes through to…&rdquo; / &ldquo;flags components on Apply&rdquo;</em> push captured data into the Estimator when you hit{' '}
            <strong>Apply to Estimate</strong>. Everything else is narrative and ships with your Quote &amp; SOW exports.</>
          )}
        </div>

        {/* v260.18 — Source Recordings card (audio attachments) */}
        {(doc?.attachments?.length || 0) > 0 && (
          <SourceRecordingsCard
            estimateId={estimateId}
            attachments={doc.attachments}
            onAfterDelete={handleAttachmentDeleted}
          />
        )}

        {/* v260.12 — Revision-nudge banner */}
        {revisionNudge && (
          <div
            className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3"
            data-testid="revision-nudge"
          >
            <ClipboardCheck className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-900">
                Add a revision entry?
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                {revisionNudge.reason}. Capture a quick note so the Document Control
                table stays in sync with your edits — stamped with today's date and{' '}
                <span className="font-medium">{user?.name || user?.email || 'your name'}</span>.
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  placeholder="e.g. Added integration scope + maturity scores"
                  value={revisionDraft}
                  onChange={(e) => setRevisionDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addRevisionEntry();
                    }
                  }}
                  className="h-8 text-xs border-amber-300 focus-visible:ring-amber-400"
                  data-testid="revision-nudge-input"
                />
                <Button
                  size="sm"
                  className="h-8 bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
                  onClick={addRevisionEntry}
                  data-testid="revision-nudge-add-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add to history
                </Button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setRevisionNudgeDismissed(true)}
              className="text-amber-700 hover:text-amber-900 p-1 -mt-1 -mr-1"
              aria-label="Dismiss revision nudge"
              data-testid="revision-nudge-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {(catalog.narrative_taxonomy || []).map((section) => (
          <NarrativeSection
            key={section.id}
            section={section}
            value={(doc.narrative || {})[section.id] || {}}
            isOpen={!!openSections[section.id]}
            onToggle={() => setOpenSections((o) => ({
              ...o, [section.id]: !o[section.id],
            }))}
            onChange={(fieldId, value) => setNarrativeField(section.id, fieldId, value)}
            mappedCtx={mappedCtx}
            provenance={(doc.field_provenance || {})[section.id] || {}}
            verifications={(doc.field_verifications || {})[section.id] || {}}
            onJumpToProvenance={jumpToProvenance}
          />
        ))}
      </div>

      {/* Email dialog */}
      <EmailRequirementsDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        estimateId={estimateId}
        estimate={{
          name: doc?.estimate_name,
          org: doc?.organization_name,
        }}
        onBeforeSend={async () => {
          if (dirtyRef.current) {
            await axios.put(`${API_URL}/api/estimates/${estimateId}/requirements`,
              { requirements_doc: doc }, { withCredentials: true });
            dirtyRef.current = false;
          }
        }}
      />

      {/* Send history dialog */}
      <SendHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        estimateId={estimateId}
      />

      {/* v260.16 — Transcript → Requirements import dialog */}
      <TranscriptImportDialog
        open={transcriptDialogOpen}
        onOpenChange={(o) => {
          setTranscriptDialogOpen(o);
          if (!o) { setResumeJobId(null); refreshLatestJob(); }
        }}
        estimateId={estimateId}
        catalog={catalog}
        onApply={handleApplyTranscript}
        resumeJobId={resumeJobId}
      />

      {/* v260.20 — Verify AI Imports review mode */}
      <VerifyImportsDialog
        open={verifyDialogOpen}
        onOpenChange={setVerifyDialogOpen}
        doc={doc}
        catalog={catalog}
        estimateId={estimateId}
        onVerify={handleVerifyAction}
      />

      {/* Apply preview modal */}
      <Dialog open={!!applyPreview} onOpenChange={(o) => !o && setApplyPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="requirements-apply-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Changes applied to estimate
            </DialogTitle>
            <DialogDescription>
              The Estimator, Project Conditions, and Project Factors have been updated. Review the changes below.
            </DialogDescription>
          </DialogHeader>
          {applyPreview && <ApplyDiff diff={applyPreview.diff} summary={applyPreview.summary} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyPreview(null)} data-testid="requirements-apply-close">
              Close
            </Button>
            <Button onClick={() => {
              // v260.38/v260.40/v260.41 — pass components_added AND
              // integrations_added with explicit prefixes so the
              // Estimator's highlight stepper can step through both
              // kinds (component rows on their group tab, integration
              // rows on the Integration Scope tab). Also stash the full
              // Apply diff in sessionStorage so the "Done · View full
              // diff" finale on the stepper can re-open the complete
              // Apply diff (auto_flagged_from_narrative +
              // deliverables_added etc.) without another roundtrip.
              const compNames = (applyPreview?.diff?.components_added || []).filter(Boolean);
              const intTypes = (applyPreview?.diff?.integrations_added || []).filter(Boolean);
              const entries = [
                ...compNames.map((n) => `comp::${n}`),
                ...intTypes.map((t) => `int::${t}`),
              ];
              try {
                sessionStorage.setItem(
                  `seer:lastApplyDiff:${estimateId}`,
                  JSON.stringify({
                    diff: applyPreview?.diff || null,
                    summary: applyPreview?.summary || null,
                    applied_at: applyPreview?.applied_at || null,
                  }),
                );
              } catch (_) { /* sessionStorage may be unavailable in some browsers */ }
              const highlight = entries.length > 0
                ? `&highlight=${entries.map(encodeURIComponent).join(',')}`
                : '';
              navigate(`/estimator?load=${estimateId}${highlight}`);
            }} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="requirements-apply-goto-estimator">
              Open Estimator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Narrative section card ──────────────────────────────────────────────
function NarrativeSection({
  section, value, isOpen, onToggle, onChange, mappedCtx,
  provenance, verifications, onJumpToProvenance,
}) {
  const pendingCount = provenance ? Object.keys(provenance).length : 0;
  const verifiedCount = verifications ? Object.keys(verifications).length : 0;
  // v260.21 — Section-level progress bar: only show when there's been
  // at least one AI import in this section (pending OR verified). If
  // pendingCount is 0 AND verifiedCount is 0 the section has nothing
  // to verify so the bar is suppressed.
  const totalImports = pendingCount + verifiedCount;
  const pct = totalImports > 0 ? Math.round((verifiedCount / totalImports) * 100) : 0;
  return (
    <Card data-testid={`narrative-section-${section.id}`}>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={onToggle}
          className="w-full flex items-center justify-between px-5 py-4 border-b border-slate-100 hover:bg-slate-50 transition-colors"
          data-testid={`narrative-toggle-${section.id}`}
        >
          <span className="text-sm font-semibold text-[#0F2040] flex items-center gap-2 flex-1 min-w-0">
            {isOpen ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
            <span className="truncate">{section.label}</span>
            {section.internal_only && (
              <Badge variant="outline" className="ml-2 border-rose-300 bg-rose-50 text-rose-700 text-[10px]">
                <Lock className="w-3 h-3 mr-1" /> Internal Only
              </Badge>
            )}
            {pendingCount > 0 && (
              <Badge
                variant="outline"
                className="border-cyan-300 bg-cyan-50 text-cyan-700 text-[10px]"
                title={`${pendingCount} field(s) AI-imported and awaiting human verification`}
              >
                <Sparkles className="w-3 h-3 mr-1" /> {pendingCount} pending
              </Badge>
            )}
            {verifiedCount > 0 && pendingCount === 0 && (
              <Badge
                variant="outline"
                className="border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px]"
                title={`${verifiedCount} field(s) human-verified`}
                data-testid={`narrative-section-verified-badge-${section.id}`}
              >
                <CheckCircle2 className="w-3 h-3 mr-1" /> All verified
              </Badge>
            )}
          </span>
          {totalImports > 0 && (
            <div
              className="flex items-center gap-2 ml-2 flex-shrink-0"
              title={`${verifiedCount} of ${totalImports} AI-imported field(s) verified`}
              data-testid={`narrative-section-progress-${section.id}`}
            >
              <div className="text-[10px] font-medium text-slate-500 w-9 text-right tabular-nums">
                {pct}%
              </div>
              <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${pct === 100 ? 'bg-emerald-500' : 'bg-cyan-500'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}
        </button>
        {isOpen && (
          <div className="p-5 space-y-5">
            {section.description && (
              <div className="text-xs text-slate-500 italic">{section.description}</div>
            )}
            {section.fields.map((field) => (
              <NarrativeField
                key={field.id}
                sectionId={section.id}
                field={field}
                value={value[field.id]}
                onChange={(v) => onChange(field.id, v)}
                mappedCtx={mappedCtx}
                provenance={(provenance || {})[field.id]}
                verification={(verifications || {})[field.id]}
                onJumpToProvenance={onJumpToProvenance}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// v260.19 — Small chip shown next to every AI-imported field. Clicking
// it scrolls to the Source Recordings card and starts playback at the
// matching audio segment (when timestamps are available).
function ProvenanceChip({ provenance, onJump, testid }) {
  if (!provenance) return null;
  const hasTime = provenance.start_seconds != null;
  const stamp = (() => {
    if (!hasTime) return null;
    const s = Math.max(0, Math.floor(provenance.start_seconds));
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  })();
  const tooltip = [
    'Imported from source recording',
    provenance.quote ? `"${provenance.quote}"` : null,
    stamp ? `Jump to ${stamp}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <button
      type="button"
      onClick={() => onJump?.(provenance)}
      title={tooltip}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 hover:border-cyan-300 transition-colors text-[10px] font-medium"
      data-testid={testid}
    >
      <Sparkles className="w-2.5 h-2.5" />
      {hasTime ? <>From recording · {stamp}</> : 'AI-imported'}
    </button>
  );
}

// v260.21 — Permanent verification stamp. Surfaces after a reviewer
// approves an AI-imported field in the Verify dialog.
function VerifiedChip({ verification, testid }) {
  if (!verification) return null;
  const when = (() => {
    if (!verification.verified_at) return null;
    try {
      return new Date(verification.verified_at).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric',
      });
    } catch { return null; }
  })();
  const tooltip = [
    verification.verified_by ? `Verified by ${verification.verified_by}` : 'Verified',
    verification.verified_at ? `on ${new Date(verification.verified_at).toLocaleString()}` : null,
  ].filter(Boolean).join(' ');
  return (
    <span
      title={tooltip}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] font-medium"
      data-testid={testid}
    >
      <CheckCircle2 className="w-2.5 h-2.5" />
      {verification.verified_by ? <>Verified by {verification.verified_by.split(' ')[0]}</> : 'Verified'}
      {when && <span className="text-emerald-600/70"> · {when}</span>}
    </span>
  );
}

function LongTextNarrativeField({
  sectionId, fieldId, labelText, label, value, onChange,
  placeholder, rows, testid,
}) {
  // v260.104 — Register this editor with the bulk-import context so
  // a multi-file drop on ANY editor in the form can write into this
  // field by its stable id. The id and setter are passed in; the
  // current value updates on every render so the bulk-import dialog
  // sees fresh content when appending.
  const id = `${sectionId}::${fieldId}`;
  useRegisterBulkImportField({
    id, label: labelText || fieldId, value, setValue: onChange,
  });
  const { openWithFiles } = useBulkImport();
  return (
    <div>
      {label}
      <RichTextEditor
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className="text-sm"
        data-testid={testid}
        onMultiFileDrop={openWithFiles}
      />
    </div>
  );
}

function NarrativeField({
  sectionId, field, value, onChange, mappedCtx,
  provenance, verification, onJumpToProvenance,
}) {
  const testid = `narr-${sectionId}-${field.id}`;
  const label = (
    <div className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-2 flex-wrap">
      <span>{field.label}</span>
      <ProvenanceChip
        provenance={provenance}
        onJump={onJumpToProvenance}
        testid={`prov-${sectionId}-${field.id}`}
      />
      <VerifiedChip
        verification={verification}
        testid={`verif-${sectionId}-${field.id}`}
      />
    </div>
  );

  if (field.kind === 'mapped_section') {
    return (
      <div data-testid={`mapped-${field.mapped_section}`}>
        <div className="text-xs font-semibold text-cyan-700 mb-2 flex items-center gap-1.5">
          <Wand2 className="w-3 h-3" />
          {field.label}
        </div>
        <div className="border border-cyan-200 bg-cyan-50/40 rounded-md p-4">
          <MappedSection mappedId={field.mapped_section} ctx={mappedCtx} />
        </div>
      </div>
    );
  }

  if (field.kind === 'text' || field.kind === 'date') {
    return (
      <div>
        {label}
        <Input
          type={field.kind === 'date' ? 'date' : 'text'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
          className="h-9 text-sm"
          data-testid={testid}
        />
      </div>
    );
  }

  if (field.kind === 'long-text') {
    return (
      <LongTextNarrativeField
        sectionId={sectionId}
        fieldId={field.id}
        labelText={field.label}
        label={label}
        value={value ?? ''}
        onChange={onChange}
        placeholder={field.placeholder || ''}
        rows={field.rows || 4}
        testid={testid}
      />
    );
  }

  if (field.kind === 'select') {
    return (
      <div>
        {label}
        <Select value={value || ''} onValueChange={(v) => onChange(v === '__clear__' ? '' : v)}>
          <SelectTrigger className="h-9 text-sm" data-testid={testid}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__clear__">— Clear —</SelectItem>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.kind === 'bullets') {
    return <BulletsField label={label} value={value} onChange={onChange} testid={testid} placeholder={field.placeholder} />;
  }

  if (field.kind === 'checkboxes') {
    return <CheckboxesField label={label} value={value} onChange={onChange} testid={testid} options={field.options || []} allowOther={field.allow_other} />;
  }

  if (field.kind === 'table') {
    return <TableField labelEl={label} field={field} value={value} onChange={onChange} testid={testid} />;
  }

  return null;
}

// ── Mapped-section dispatcher: renders the appropriate inline UI ────────
function MappedSection({ mappedId, ctx }) {
  if (mappedId === 'timeline') return <TimelineTargetsMapped ctx={ctx} />;
  if (mappedId === 'maturity') return <MaturityAssessmentMapped ctx={ctx} />;
  if (mappedId === 'functional') return <FunctionalRequirementsMapped ctx={ctx} />;
  if (mappedId === 'ojt') return <OJTMapped ctx={ctx} />;
  if (mappedId === 'integrations') return <IntegrationsMapped ctx={ctx} />;
  if (mappedId === 'migration') return <MigrationMapped ctx={ctx} />;
  return null;
}

function TimelineTargetsMapped({ ctx }) {
  const { doc, setField } = ctx;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="mapped-timeline-grid">
      <SimpleField label="Implementation Start" type="date" testid="timeline-start"
        value={doc.timeline_targets?.implementation_start || ''}
        onChange={(v) => setField('timeline_targets', 'implementation_start', v)} />
      <SimpleField label="Go-Live" type="date" testid="timeline-golive"
        value={doc.timeline_targets?.go_live || ''}
        onChange={(v) => setField('timeline_targets', 'go_live', v)} />
      <SimpleField label="Estimated Duration (weeks)" type="number" testid="timeline-duration"
        value={doc.timeline_targets?.duration_weeks ?? ''}
        onChange={(v) => setField('timeline_targets', 'duration_weeks', v ? parseInt(v, 10) : null)} />
    </div>
  );
}

function MaturityAssessmentMapped({ ctx }) {
  const { doc, catalog, setSection, estimate } = ctx;
  const [baselineOpen, setBaselineOpen] = useState(false);
  const orgId = estimate?.org_id || null;
  const orgName = estimate?.organization_name || null;

  const handleApplyBaseline = (rows) => {
    // Merge baseline rows into the current maturity_assessment array.
    const cur = doc.maturity_assessment || [];
    const byKey = new Map(cur.map((m) => [m.factor_key, m]));
    rows.forEach((r) => {
      const prev = byKey.get(r.factor_key) || { factor_key: r.factor_key, level: '', notes: '' };
      byKey.set(r.factor_key, { ...prev, level: r.level, notes: r.notes || prev.notes });
    });
    setSection('maturity_assessment', Array.from(byKey.values()));
  };

  return (
    <div className="space-y-3" data-testid="mapped-maturity-grid">
      {orgId ? (
        <div className="flex items-center justify-between bg-cyan-50/40 border border-cyan-200 rounded px-3 py-2 mb-1">
          <div className="flex items-center gap-2 text-xs text-cyan-900">
            <Gauge className="w-4 h-4 text-cyan-700" />
            Tip: use the customer's maturity assessment as a baseline for these factors.
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setBaselineOpen(true)}
            className="border-cyan-300 text-cyan-800 hover:bg-cyan-100 h-7 text-xs"
            data-testid="maturity-baseline-trigger"
          >
            Use maturity baseline…
          </Button>
        </div>
      ) : null}
      {catalog.maturity_factors.map((f) => {
        const cur = (doc.maturity_assessment || []).find((m) => m.factor_key === f.factor_key)
          || { factor_key: f.factor_key, level: '', notes: '' };
        const update = (patch) => {
          const arr = [...(doc.maturity_assessment || [])];
          const idx = arr.findIndex((m) => m.factor_key === f.factor_key);
          if (idx >= 0) arr[idx] = { ...arr[idx], ...patch };
          else arr.push({ factor_key: f.factor_key, level: '', notes: '', ...patch });
          setSection('maturity_assessment', arr);
        };
        return (
          <div key={f.factor_key} className="grid grid-cols-12 gap-2 items-center" data-testid={`maturity-row-${f.factor_key}`}>
            <div className="col-span-12 md:col-span-4 text-sm font-medium text-slate-700">{f.label}
              <span className="ml-1 text-[10px] text-slate-400 uppercase tracking-wide">{f.kind}</span>
            </div>
            <div className="col-span-12 md:col-span-3 flex gap-1">
              {['low', 'medium', 'high'].map((lv) => (
                <button
                  key={lv}
                  type="button"
                  onClick={() => update({ level: cur.level === lv ? '' : lv })}
                  className={`flex-1 text-xs px-2 py-1.5 rounded border transition-colors ${
                    cur.level === lv
                      ? 'bg-cyan-50 border-cyan-300 text-cyan-700 font-semibold'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`maturity-level-${f.factor_key}-${lv}`}
                >
                  {lv[0].toUpperCase() + lv.slice(1)}
                </button>
              ))}
            </div>
            <div className="col-span-12 md:col-span-5">
              <Input
                placeholder="Notes (optional)"
                value={cur.notes || ''}
                onChange={(e) => update({ notes: e.target.value })}
                className="h-8 text-xs"
                data-testid={`maturity-notes-${f.factor_key}`}
              />
            </div>
          </div>
        );
      })}
      <MaturityBaselineDialog
        open={baselineOpen}
        onOpenChange={setBaselineOpen}
        organizationId={orgId}
        organizationName={orgName}
        currentMaturity={doc.maturity_assessment}
        onApply={handleApplyBaseline}
      />
    </div>
  );
}

function FunctionalRequirementsMapped({ ctx }) {
  const { doc, catalog, toggleSelection, findSelection, suggestions,
          bulkAcceptSuggestions, dismissSuggestion, restoreDismissedInSection } = ctx;
  const sectionItemIds = useMemo(() => {
    const ids = [];
    for (const sub of catalog.functional_sections) for (const it of sub.items) ids.push(it.id);
    return ids;
  }, [catalog]);
  const dismissedCount = useMemo(() => {
    const ids = new Set(sectionItemIds);
    return (doc?.dismissed_suggestions || []).filter((x) => ids.has(x)).length;
  }, [doc, sectionItemIds]);
  const pendingIds = useMemo(() => {
    const out = [];
    for (const sub of catalog.functional_sections) {
      for (const it of sub.items) {
        if (suggestions?.[it.id] && !findSelection('functional_requirements', it.id).selected) {
          out.push(it.id);
        }
      }
    }
    return out;
  }, [catalog, suggestions, findSelection]);
  return (
    <div className="space-y-5" data-testid="mapped-functional-grid">
      <AcceptAllSuggestionsBar
        count={pendingIds.length}
        onAccept={() => bulkAcceptSuggestions('functional_requirements', pendingIds)}
        testid="accept-all-functional"
      />
      <DismissedSummaryLine
        count={dismissedCount}
        onRestore={() => restoreDismissedInSection(sectionItemIds)}
        testid="restore-dismissed-functional"
      />
      {catalog.functional_sections.map((sub) => (
        <div key={sub.id}>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{sub.label}</div>
          {sub.items.length === 0 && (
            <div className="text-xs text-slate-400 italic">No catalog items.</div>
          )}
          <div className="space-y-1">
            {sub.items.map((it) => (
              <SelectableRow
                key={it.id}
                item={it}
                sectionKey="functional_requirements"
                sel={findSelection('functional_requirements', it.id)}
                onToggle={toggleSelection}
                suggestion={suggestions?.[it.id]}
                onDismissSuggestion={dismissSuggestion}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function OJTMapped({ ctx }) {
  const { doc, catalog, toggleSelection, findSelection, suggestions,
          bulkAcceptSuggestions, dismissSuggestion, restoreDismissedInSection } = ctx;
  const sectionItemIds = useMemo(() => catalog.ojt_items.map((it) => it.id), [catalog]);
  const dismissedCount = useMemo(() => {
    const ids = new Set(sectionItemIds);
    return (doc?.dismissed_suggestions || []).filter((x) => ids.has(x)).length;
  }, [doc, sectionItemIds]);
  const pendingIds = useMemo(() => catalog.ojt_items
    .filter((it) => suggestions?.[it.id] && !findSelection('ojt_requirements', it.id).selected)
    .map((it) => it.id), [catalog, suggestions, findSelection]);
  return (
    <div className="space-y-1" data-testid="mapped-ojt-grid">
      <AcceptAllSuggestionsBar
        count={pendingIds.length}
        onAccept={() => bulkAcceptSuggestions('ojt_requirements', pendingIds)}
        testid="accept-all-ojt"
      />
      <DismissedSummaryLine
        count={dismissedCount}
        onRestore={() => restoreDismissedInSection(sectionItemIds)}
        testid="restore-dismissed-ojt"
      />
      {catalog.ojt_items.map((it) => (
        <SelectableRow
          key={it.id}
          item={it}
          sectionKey="ojt_requirements"
          sel={findSelection('ojt_requirements', it.id)}
          onToggle={toggleSelection}
          withComplexity
          suggestion={suggestions?.[it.id]}
          onDismissSuggestion={dismissSuggestion}
        />
      ))}
    </div>
  );
}

function IntegrationsMapped({ ctx }) {
  const { doc, catalog, toggleSelection, findSelection, suggestions,
          bulkAcceptSuggestions, dismissSuggestion, restoreDismissedInSection } = ctx;
  const sectionItemIds = useMemo(() => catalog.integrations.map((it) => it.id), [catalog]);
  const dismissedCount = useMemo(() => {
    const ids = new Set(sectionItemIds);
    return (doc?.dismissed_suggestions || []).filter((x) => ids.has(x)).length;
  }, [doc, sectionItemIds]);
  const pendingIds = useMemo(() => catalog.integrations
    .filter((it) => suggestions?.[it.id] && !findSelection('integrations', it.id).selected)
    .map((it) => it.id), [catalog, suggestions, findSelection]);
  return (
    <div className="space-y-1" data-testid="mapped-integrations-grid">
      <AcceptAllSuggestionsBar
        count={pendingIds.length}
        onAccept={() => bulkAcceptSuggestions('integrations', pendingIds)}
        testid="accept-all-integrations"
      />
      <DismissedSummaryLine
        count={dismissedCount}
        onRestore={() => restoreDismissedInSection(sectionItemIds)}
        testid="restore-dismissed-integrations"
      />
      {catalog.integrations.map((it) => (
        <SelectableRow
          key={it.id}
          item={it}
          sectionKey="integrations"
          sel={findSelection('integrations', it.id)}
          onToggle={toggleSelection}
          withComplexity
          suggestion={suggestions?.[it.id]}
          onDismissSuggestion={dismissSuggestion}
        />
      ))}
    </div>
  );
}

function MigrationMapped({ ctx }) {
  const { doc, catalog, setField, toggleSelection, findSelection, suggestions,
          bulkAcceptSuggestions, dismissSuggestion, restoreDismissedInSection } = ctx;
  const sectionItemIds = useMemo(() => catalog.migration_items.map((it) => it.id), [catalog]);
  const dismissedCount = useMemo(() => {
    const ids = new Set(sectionItemIds);
    return (doc?.dismissed_suggestions || []).filter((x) => ids.has(x)).length;
  }, [doc, sectionItemIds]);
  const pendingIds = useMemo(() => catalog.migration_items
    .filter((it) => suggestions?.[it.id] && !findSelection('migration_scope', it.id).selected)
    .map((it) => it.id), [catalog, suggestions, findSelection]);
  return (
    <div className="space-y-3" data-testid="mapped-migration-grid">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">11.1 Migration Strategy</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {['full', 'partial', 'fresh_start'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setField('data_migration', 'strategy', doc.data_migration?.strategy === s ? '' : s)}
            className={`text-sm px-3 py-2 rounded border transition-colors ${
              doc.data_migration?.strategy === s
                ? 'bg-cyan-50 border-cyan-300 text-cyan-700 font-semibold'
                : 'border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
            data-testid={`migration-strategy-${s}`}
          >
            {s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
          </button>
        ))}
      </div>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">11.2 Migration Scope</div>
      <AcceptAllSuggestionsBar
        count={pendingIds.length}
        onAccept={() => bulkAcceptSuggestions('migration_scope', pendingIds)}
        testid="accept-all-migration"
      />
      <DismissedSummaryLine
        count={dismissedCount}
        onRestore={() => restoreDismissedInSection(sectionItemIds)}
        testid="restore-dismissed-migration"
      />
      <div className="space-y-1">
        {catalog.migration_items.map((it) => (
          <SelectableRow
            key={it.id}
            item={it}
            sectionKey="migration_scope"
            sel={findSelection('migration_scope', it.id)}
            onToggle={toggleSelection}
            withComplexity
            suggestion={suggestions?.[it.id]}
            onDismissSuggestion={dismissSuggestion}
          />
        ))}
      </div>
    </div>
  );
}

// v260.34 — Header pill that bulk-accepts every narrative-driven
// suggestion in the surrounding mapped section. Renders nothing when
// there are no pending suggestions, so the layout stays clean once the
// user has accepted or dismissed everything.
function AcceptAllSuggestionsBar({ count, onAccept, testid }) {
  if (!count) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-2.5 py-2 rounded border border-amber-200 bg-amber-50/60" data-testid={`${testid}-bar`}>
      <div className="flex items-center gap-2 text-xs text-amber-800">
        <Sparkles className="w-3.5 h-3.5" />
        <span><strong>{count}</strong> narrative-driven suggestion{count === 1 ? '' : 's'} pending</span>
      </div>
      <button
        type="button"
        onClick={onAccept}
        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded border border-amber-300 bg-white text-amber-700 hover:bg-amber-100 transition-colors"
        data-testid={testid}
      >
        Accept all {count}
      </button>
    </div>
  );
}

// v260.36 — Slim header line surfacing the count of dismissed
// suggestions for the surrounding mapped section + a one-click Restore
// action. Renders nothing when nothing is dismissed so the layout stays
// quiet until there's actually something to undo.
function DismissedSummaryLine({ count, onRestore, testid }) {
  if (!count) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-2 py-1 text-[11px] text-slate-500" data-testid={`${testid}-line`}>
      <span>
        <strong className="text-slate-600">{count}</strong> suggestion{count === 1 ? '' : 's'} dismissed for this estimate
      </span>
      <button
        type="button"
        onClick={onRestore}
        className="text-cyan-700 hover:text-cyan-800 hover:underline font-medium"
        data-testid={testid}
      >
        Restore
      </button>
    </div>
  );
}

// ── Bullets (list of strings) ───────────────────────────────────────────
function BulletsField({ label, value, onChange, testid, placeholder }) {
  const arr = Array.isArray(value) ? value : [];
  const update = (idx, v) => {
    const next = [...arr];
    next[idx] = v;
    onChange(next);
  };
  const remove = (idx) => onChange(arr.filter((_, i) => i !== idx));
  const add = () => onChange([...arr, '']);
  return (
    <div>
      {label}
      <div className="space-y-1.5">
        {arr.map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="text-slate-300 text-sm pt-2 select-none">•</span>
            <Input
              value={item}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder || 'Add detail…'}
              className="h-8 text-sm flex-1"
              data-testid={`${testid}-bullet-${i}`}
            />
            <Button
              variant="ghost" size="sm"
              onClick={() => remove(i)}
              className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600"
              data-testid={`${testid}-remove-${i}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        <Button
          variant="outline" size="sm"
          onClick={add}
          className="h-8 text-xs border-dashed text-slate-500"
          data-testid={`${testid}-add`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add bullet
        </Button>
      </div>
    </div>
  );
}

// Multi-select checkboxes with a fixed set of options. Storage is a
// list[str] of selected option ids (or labels) — same shape as bullets,
// so AI extractors and the existing serialization paths keep working.
// `allow_other` adds a free-text "Other" row that bubbles into the list
// prefixed with `Other: `.
function CheckboxesField({ label, value, onChange, testid, options, allowOther }) {
  const arr = Array.isArray(value) ? value : [];
  const selected = new Set(arr.filter(v => !v.startsWith('Other: ')));
  const otherValue = (arr.find(v => v.startsWith('Other: ')) || '').replace(/^Other:\s*/, '');
  const toggle = (opt) => {
    const next = new Set(selected);
    if (next.has(opt)) next.delete(opt); else next.add(opt);
    const out = Array.from(next);
    if (otherValue) out.push(`Other: ${otherValue}`);
    onChange(out);
  };
  const updateOther = (newOther) => {
    const out = Array.from(selected);
    if (newOther && newOther.trim()) out.push(`Other: ${newOther.trim()}`);
    onChange(out);
  };
  return (
    <div>
      {label}
      <div className="space-y-1.5" data-testid={testid}>
        {options.map((opt) => (
          <label
            key={opt}
            className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-[#0F2040]"
            data-testid={`${testid}-opt-${opt.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
          >
            <Checkbox
              checked={selected.has(opt)}
              onCheckedChange={() => toggle(opt)}
            />
            <span>{opt}</span>
          </label>
        ))}
        {allowOther && (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-slate-500 w-12">Other:</span>
            <Input
              value={otherValue}
              onChange={(e) => updateOther(e.target.value)}
              placeholder="Type a custom value…"
              className="h-8 text-sm flex-1"
              data-testid={`${testid}-other`}
            />
          </div>
        )}
      </div>
    </div>
  );
}


// ── Dynamic table ───────────────────────────────────────────────────────
function TableField({ labelEl, field, value, onChange, testid }) {
  const rows = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(field.seed_rows) && field.seed_rows.length > 0) {
      return field.seed_rows.map((r) => ({ ...r }));
    }
    return [];
  }, [value, field.seed_rows]);

  const seededOnMount = useRef(false);
  useEffect(() => {
    if (!seededOnMount.current && value === undefined && rows.length > 0) {
      seededOnMount.current = true;
      onChange(rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCell = (rowIdx, colId, cellValue) => {
    const next = rows.map((r, i) => (i === rowIdx ? { ...r, [colId]: cellValue } : r));
    onChange(next);
  };
  const removeRow = (rowIdx) => onChange(rows.filter((_, i) => i !== rowIdx));
  const addRow = () => {
    const blank = {};
    field.columns.forEach((c) => { blank[c.id] = ''; });
    onChange([...rows, blank]);
  };

  return (
    <div>
      {labelEl}
      {field.note && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
          {field.note}
        </div>
      )}
      <div className="border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {field.columns.map((col) => (
                <th key={col.id} className="text-left px-3 py-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap">
                  {col.label}
                </th>
              ))}
              <th className="w-10 px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={field.columns.length + 1} className="px-3 py-4 text-xs text-slate-400 italic text-center">
                  No rows yet — click "{field.addRowLabel || 'Add row'}" below.
                </td>
              </tr>
            )}
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} className="border-b border-slate-100 last:border-b-0" data-testid={`${testid}-row-${rowIdx}`}>
                {field.columns.map((col) => (
                  <td key={col.id} className="px-2 py-1.5 align-top">
                    <TableCell
                      col={col}
                      value={row[col.id] ?? ''}
                      onChange={(v) => updateCell(rowIdx, col.id, v)}
                      testid={`${testid}-cell-${rowIdx}-${col.id}`}
                    />
                  </td>
                ))}
                <td className="px-2 py-1.5 align-top">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => removeRow(rowIdx)}
                    className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
                    data-testid={`${testid}-remove-${rowIdx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button
        variant="outline" size="sm"
        onClick={addRow}
        className="h-8 text-xs border-dashed text-slate-500 mt-2"
        data-testid={`${testid}-add-row`}
      >
        <Plus className="w-3.5 h-3.5 mr-1" /> {field.addRowLabel || 'Add row'}
      </Button>
    </div>
  );
}

function TableCell({ col, value, onChange, testid }) {
  if (col.kind === 'select') {
    return (
      <Select value={value || ''} onValueChange={(v) => onChange(v === '__clear__' ? '' : v)}>
        <SelectTrigger className="h-8 text-xs min-w-[120px]" data-testid={testid}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__clear__">— Clear —</SelectItem>
          {(col.options || []).map((opt) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (col.kind === 'date') {
    return (
      <DatePickerField
        value={value || ''}
        onChange={onChange}
        testId={testid}
        placeholder="Pick a date"
        widthClass="w-full"
      />
    );
  }
  if (col.kind === 'long-text') {
    return (
      <RichTextEditor
        value={value}
        onChange={onChange}
        rows={2}
        className="text-xs"
        placeholder={col.placeholder || ''}
        data-testid={testid}
      />
    );
  }
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 text-xs"
      placeholder={col.placeholder || ''}
      data-testid={testid}
    />
  );
}

function SimpleField({ label, value, onChange, type = 'text', testid }) {
  return (
    <div>
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      {type === 'date' ? (
        <DatePickerField
          value={value ?? ''}
          onChange={onChange}
          testId={testid}
          placeholder="Pick a date"
          widthClass="w-full"
        />
      ) : (
        <Input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          className="h-9 text-sm" data-testid={testid} />
      )}
    </div>
  );
}

function SelectableRow({ item, sectionKey, sel, onToggle, withComplexity, suggestion, onDismissSuggestion }) {
  // v260.33 — Phase 2: show a "Suggested" sparkle pill when the
  // narrative mentions the keyword(s) for this catalog row but the user
  // hasn't selected it yet. Clicking the pill toggles selection (acts
  // as a one-click accept). Tooltip carries the source quote so the PM
  // can see the originating sentence.
  // v260.35 — Sibling × button (hover-revealed on the row) dismisses
  // the suggestion for this estimate. Persists via
  // doc.dismissed_suggestions so the pill stops re-appearing.
  const showSuggestion = !!suggestion && !sel.selected;
  return (
    <div className="group flex items-start gap-2 py-1.5 px-2 rounded hover:bg-slate-50">
      <Checkbox
        checked={!!sel.selected}
        onCheckedChange={(checked) => onToggle(sectionKey, item.id, { selected: !!checked })}
        className="mt-0.5"
        data-testid={`req-check-${item.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm text-slate-800">{item.label}</div>
          {showSuggestion && (
            <div className="inline-flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => onToggle(sectionKey, item.id, { selected: true })}
                title={`Suggested by ${suggestion.source_label}: "${suggestion.quote}"`}
                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide pl-1.5 pr-2 py-0.5 rounded-l border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                data-testid={`req-suggested-${item.id}`}
              >
                <Sparkles className="w-3 h-3" />
                Suggested
              </button>
              {onDismissSuggestion && (
                <button
                  type="button"
                  onClick={() => onDismissSuggestion(item.id)}
                  title="Dismiss this suggestion for this estimate"
                  aria-label="Dismiss suggestion"
                  className="inline-flex items-center justify-center h-[20px] w-[20px] -ml-px rounded-r border border-amber-300 bg-amber-50 text-amber-700/70 opacity-0 group-hover:opacity-100 hover:bg-amber-100 hover:text-amber-800 transition-opacity focus:opacity-100"
                  data-testid={`req-dismiss-${item.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>
        {showSuggestion && (
          <div className="text-[11px] text-amber-700/80 mt-0.5 truncate" title={suggestion.quote}>
            {suggestion.source_label}: “{suggestion.quote}”
          </div>
        )}
        {sel.selected && (
          <div className="mt-1 flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <RichTextEditor
                value={sel.comment || ''}
                onChange={(md) => onToggle(sectionKey, item.id, { comment: md })}
                placeholder="Comment / scope notes (optional)"
                rows={2}
                className="text-xs"
                compact
                data-testid={`req-comment-${item.id}`}
              />
            </div>
            {withComplexity && (
              <div className="flex gap-1 mt-1.5">
                {['low', 'medium', 'high'].map((lv) => (
                  <button
                    key={lv}
                    type="button"
                    onClick={() => onToggle(sectionKey, item.id, { complexity: sel.complexity === lv ? '' : lv })}
                    className={`text-[10px] px-1.5 py-0.5 rounded border ${
                      sel.complexity === lv
                        ? 'bg-cyan-50 border-cyan-300 text-cyan-700 font-semibold'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                    data-testid={`req-complexity-${item.id}-${lv}`}
                  >
                    {lv[0].toUpperCase()}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Email Requirements dialog ─────────────────────────────────────
function EmailRequirementsDialog({ open, onClose, estimateId, estimate, onBeforeSend }) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [includePdf, setIncludePdf] = useState(true);
  const [includeDocx, setIncludeDocx] = useState(true);
  const [sending, setSending] = useState(false);

  // Reset on open.
  useEffect(() => {
    if (open) {
      setRecipientEmail(''); setRecipientName('');
      setSubject(''); setMessage('');
      setIncludePdf(true); setIncludeDocx(true);
      setSending(false);
    }
  }, [open]);

  const canSend = recipientEmail.includes('@') && (includePdf || includeDocx) && !sending;

  const handleSend = async () => {
    setSending(true);
    try {
      await onBeforeSend?.();
      const formats = [];
      if (includePdf) formats.push('pdf');
      if (includeDocx) formats.push('docx');
      const r = await axios.post(
        `${API_URL}/api/estimates/${estimateId}/requirements/email`,
        {
          recipient_email: recipientEmail.trim(),
          recipient_name: recipientName.trim() || null,
          subject: subject.trim() || null,
          message: message.trim() || null,
          formats,
        },
        { withCredentials: true },
      );
      toast.success(`Requirements sent to ${r.data.recipient_email}`);
      onClose?.();
    } catch (e) {
      toast.error('Email failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="requirements-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-cyan-600" />
            Email Requirements Document
          </DialogTitle>
          <DialogDescription>
            Send the captured requirements as an attachment to a stakeholder. Every send is logged for audit.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-semibold text-slate-600 mb-1">Recipient Email *</div>
              <Input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="stakeholder@company.com"
                className="h-9 text-sm"
                data-testid="email-recipient-input"
              />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-600 mb-1">Recipient Name (optional)</div>
              <Input
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Jane Doe"
                className="h-9 text-sm"
                data-testid="email-recipient-name-input"
              />
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">Subject</div>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`Requirements Document — ${estimate?.org || 'your project'}`}
              className="h-9 text-sm"
              data-testid="email-subject-input"
            />
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">Cover Note (optional)</div>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi — sharing the captured requirements ahead of our next session…"
              rows={4}
              className="text-sm"
              data-testid="email-message-input"
            />
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1.5">Attachments</div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox
                  checked={includePdf}
                  onCheckedChange={(c) => setIncludePdf(!!c)}
                  data-testid="email-include-pdf"
                />
                PDF
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox
                  checked={includeDocx}
                  onCheckedChange={(c) => setIncludeDocx(!!c)}
                  data-testid="email-include-docx"
                />
                DOCX (editable)
              </label>
            </div>
            {!includePdf && !includeDocx && (
              <div className="text-[11px] text-rose-600 mt-1">Pick at least one format.</div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="email-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!canSend}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="email-send-btn"
          >
            {sending ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>)
              : (<><Mail className="w-4 h-4 mr-2" /> Send</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Send History dialog ────────────────────────────────────────────
function SendHistoryDialog({ open, onClose, estimateId }) {
  const [sends, setSends] = useState(null);
  const [resending, setResending] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API_URL}/api/estimates/${estimateId}/requirements/sends`,
        { withCredentials: true },
      );
      setSends(r.data.sends || []);
    } catch (e) {
      toast.error('Failed to load send history: ' + (e.response?.data?.detail || e.message));
      setSends([]);
    }
  }, [estimateId]);

  useEffect(() => {
    if (open) {
      setSends(null);
      refresh();
    }
  }, [open, refresh]);

  const handleResend = async (sendId) => {
    setResending(sendId);
    try {
      const r = await axios.post(
        `${API_URL}/api/estimates/${estimateId}/requirements/sends/${sendId}/resend`,
        {}, { withCredentials: true },
      );
      toast.success(`Resent to ${r.data.recipient_email}`);
      await refresh();
    } catch (e) {
      toast.error('Resend failed: ' + (e.response?.data?.detail || e.message));
    } finally { setResending(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="requirements-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-cyan-600" />
            Send History
          </DialogTitle>
          <DialogDescription>
            Every time the Requirements Document was emailed from this estimate. Click <strong>Resend</strong> to re-send to the same recipient with the original cover note.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
          {sends === null && (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}
          {sends && sends.length === 0 && (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="history-empty">
              No emails sent yet. Use the <strong>Email</strong> button to share the requirements with a stakeholder.
            </div>
          )}
          {sends && sends.length > 0 && (
            <div className="space-y-2" data-testid="history-list">
              {sends.map((s) => (
                <SendHistoryRow
                  key={s.send_id || s.email_id || s.sent_at}
                  send={s}
                  resending={resending === s.send_id}
                  onResend={() => handleResend(s.send_id)}
                />
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="history-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SendHistoryRow({ send, resending, onResend }) {
  const status = send.status || 'sent';
  const tone = STATUS_TONE[status] || STATUS_TONE.sent;
  const sentAt = send.sent_at ? new Date(send.sent_at) : null;
  const lastEvent = send.last_event_at ? new Date(send.last_event_at) : null;
  const formatLabel = (send.formats || []).map((f) => f.toUpperCase()).join(' + ') || '—';
  return (
    <div
      className="border border-slate-200 rounded-md px-3 py-2.5 bg-white hover:border-slate-300 transition-colors"
      data-testid={`history-row-${send.send_id || send.email_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold text-slate-800 truncate">
              {send.recipient_name ? `${send.recipient_name} · ` : ''}{send.recipient_email}
            </div>
            <Badge
              variant="outline"
              className={`text-[10px] uppercase tracking-wide ${tone.cls}`}
              data-testid={`history-status-${send.send_id || send.email_id}`}
            >
              {tone.label}
            </Badge>
            {send.resent_from && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-wide bg-slate-50 text-slate-500 border-slate-200">
                Resent
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{sentAt ? sentAt.toLocaleString() : '—'}</span>
            <span className="text-slate-300">·</span>
            <span>{formatLabel}</span>
            {lastEvent && (
              <>
                <span className="text-slate-300">·</span>
                <span>last event {lastEvent.toLocaleString()}</span>
              </>
            )}
          </div>
          {send.subject && (
            <div className="text-xs text-slate-600 mt-1 truncate" title={send.subject}>
              <span className="text-slate-400">Subject:</span> {send.subject}
            </div>
          )}
          {send.error && (
            <div className="text-[11px] text-rose-700 mt-1 flex items-start gap-1">
              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{send.error}</span>
            </div>
          )}
        </div>
        <Button
          variant="outline" size="sm"
          onClick={onResend}
          disabled={resending}
          className="h-8 text-xs border-slate-300 shrink-0"
          data-testid={`history-resend-${send.send_id || send.email_id}`}
        >
          {resending ? (<><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Sending…</>)
            : (<><RotateCw className="w-3.5 h-3.5 mr-1" /> Resend</>)}
        </Button>
      </div>
    </div>
  );
}

const STATUS_TONE = {
  sent: { label: 'Sent', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  delivered: { label: 'Delivered', cls: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
  delivery_delayed: { label: 'Delayed', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  opened: { label: 'Opened', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  clicked: { label: 'Clicked', cls: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
  bounced: { label: 'Bounced', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  complained: { label: 'Spam', cls: 'bg-rose-50 text-rose-800 border-rose-300' },
  failed: { label: 'Failed', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

function ApplyDiff({ diff, summary }) {
  const sections = [
    ['Project Conditions', diff.project_conditions, summary.project_conditions],
    ['Project Factors', diff.modifiers, summary.modifiers],
    ['Components added', diff.components_added, summary.components_added],
    ['Deliverables added', diff.deliverables_added, summary.deliverables_added],
    ['Integrations added', diff.integrations_added, summary.integrations_added],
    ['Auto-flagged from narrative', diff.auto_flagged_from_narrative, summary.auto_flagged_from_narrative],
    ['Skipped', diff.skipped, summary.skipped],
  ];
  return (
    <div className="space-y-3 max-h-[50vh] overflow-y-auto" data-testid="requirements-apply-diff">
      {sections.map(([label, items, count]) => (
        items?.length > 0 ? (
          <div key={label}>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              {label} · {count}
            </div>
            <ul className="text-sm space-y-0.5 pl-4">
              {items.map((line, i) => (
                <li key={i} className="text-slate-700 list-disc">{line}</li>
              ))}
            </ul>
          </div>
        ) : null
      ))}
      {Object.values(summary).every((v) => v === 0) && (
        <div className="text-sm text-slate-500 italic">No changes — the estimate already reflects this requirements doc.</div>
      )}
    </div>
  );
}
